import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Phone, Calendar, Shield, CreditCard,
  Users as UsersIcon, Trophy, Activity, Loader2, Clock, CheckCircle,
  LogIn, PlayCircle, Award, Building2, Edit3, Save, X, Sparkles
} from 'lucide-react';

const AdminUserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('children');

  // Sessions tab state
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionFilter, setSessionFilter] = useState(''); // '' | 'completed' | 'in_progress'

  // Subscription dialog state
  const [subDialogOpen, setSubDialogOpen] = useState(false);
  const [subForm, setSubForm] = useState({ plan: 'free', status: 'none', current_period_end: '', trial_end: '', reason: '' });
  const [subSaving, setSubSaving] = useState(false);

  useEffect(() => { load(); }, [userId]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/users/${userId}/profile`);
      setProfile(res.data);
      setSubForm({
        plan: res.data.subscription?.plan || 'free',
        status: res.data.subscription?.status || 'none',
        current_period_end: res.data.subscription?.current_period_end?.split('T')[0] || '',
        trial_end: res.data.subscription?.trial_end?.split('T')[0] || '',
        reason: '',
      });
    } catch (err) {
      toast.error('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async (filter = '') => {
    setSessionsLoading(true);
    try {
      const qs = filter ? `?status=${filter}&limit=100` : '?limit=100';
      const res = await api.get(`/admin/users/${userId}/sessions${qs}`);
      setSessions(res.data?.sessions || []);
    } catch (err) {
      toast.error('Failed to load sessions');
    } finally {
      setSessionsLoading(false);
    }
  };

  const handleTileClick = (target) => {
    if (target === 'children' || target === 'xp') {
      setActiveTab('children');
    } else {
      // 'activities' or 'completed'
      const filter = target === 'completed' ? 'completed' : '';
      setSessionFilter(filter);
      setActiveTab('activity');
      loadSessions(filter);
    }
  };

  const handleTabChange = (v) => {
    setActiveTab(v);
    if (v === 'activity' && sessions.length === 0) {
      loadSessions(sessionFilter);
    }
  };

  const applySubFilter = (f) => {
    setSessionFilter(f);
    loadSessions(f);
  };

  const saveSubscription = async () => {
    setSubSaving(true);
    try {
      const payload = {
        plan: subForm.plan,
        status: subForm.status,
        current_period_end: subForm.current_period_end ? `${subForm.current_period_end}T00:00:00Z` : '',
        trial_end: subForm.trial_end ? `${subForm.trial_end}T00:00:00Z` : '',
        reason: subForm.reason || null,
      };
      const res = await api.patch(`/admin/users/${userId}/subscription`, payload);
      setProfile((p) => ({ ...p, subscription: { ...p.subscription, ...res.data.subscription } }));
      toast.success('Subscription updated');
      setSubDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setSubSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }
  if (!profile) return null;

  const { user, subscription, organization, login_history, children, stats } = profile;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-to-admin">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-semibold text-slate-800">User Profile</h1>
            <p className="text-xs text-slate-500">{user.email}</p>
          </div>
          {user.is_admin && (
            <Badge className="bg-purple-100 text-purple-700"><Shield className="w-3 h-3 mr-1" /> Admin</Badge>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Hero */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-5">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center text-white text-3xl font-bold shadow">
                {(user.name || user.email || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-2xl font-bold text-slate-800">{user.name || 'Unnamed'}</h2>
                <div className="flex flex-wrap gap-3 mt-1 text-sm text-slate-600">
                  <span className="flex items-center gap-1"><Mail className="w-4 h-4" /> {user.email}</span>
                  {user.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" /> {user.phone}</span>}
                  <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Joined {(user.created_at || '').split('T')[0] || 'unknown'}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Badge variant="outline" className="capitalize">{subscription.plan}</Badge>
                  {subscription.status && <Badge className="bg-emerald-100 text-emerald-700">{subscription.status}</Badge>}
                  {user.email_verified && <Badge className="bg-blue-100 text-blue-700">Verified</Badge>}
                  {organization && (
                    <Badge className="bg-amber-100 text-amber-800 flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> {organization.name}
                    </Badge>
                  )}
                </div>
              </div>
              <Button
                onClick={() => setSubDialogOpen(true)}
                className="hidden md:flex bg-emerald-500 hover:bg-emerald-600"
                data-testid="manage-subscription-btn"
              >
                <Edit3 className="w-4 h-4 mr-2" /> Manage Subscription
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Clickable stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile label="Children" value={stats.total_children} icon={UsersIcon} color="emerald" onClick={() => handleTileClick('children')} testId="tile-children" />
          <StatTile label="Activities" value={stats.total_activities} icon={Activity} color="blue" onClick={() => handleTileClick('activities')} testId="tile-activities" />
          <StatTile label="Completed" value={stats.total_completed} icon={CheckCircle} color="teal" onClick={() => handleTileClick('completed')} testId="tile-completed" />
          <StatTile label="Total XP" value={stats.total_xp} icon={Trophy} color="amber" onClick={() => handleTileClick('xp')} testId="tile-xp" />
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList>
            <TabsTrigger value="children" data-testid="tab-children">Children</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Recent Activity</TabsTrigger>
            <TabsTrigger value="subscription" data-testid="tab-subscription">Subscription</TabsTrigger>
            <TabsTrigger value="logins" data-testid="tab-logins">Login History</TabsTrigger>
          </TabsList>

          <TabsContent value="children">
            {children.length === 0 && (
              <Card><CardContent className="p-8 text-center text-slate-500">No children on this account.</CardContent></Card>
            )}
            <div className="space-y-4 mt-4">
              {children.map((c) => (
                <Card key={c.id} className="border-0 shadow-sm" data-testid={`admin-child-card-${c.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold shadow"
                          style={{ backgroundColor: c.avatar_color || '#10B981' }}
                        >
                          {(c.avatar && c.avatar.length <= 2) ? c.avatar : (c.name || 'L')[0].toUpperCase()}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{c.name}</CardTitle>
                          <p className="text-xs text-slate-500 capitalize">
                            {c.age_group?.replace(/_/g, ' ')} · Level {c.level} · {c.total_xp} XP · Streak {c.daily_streak}d
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-xs text-slate-500">
                        <div>Sessions: <span className="font-semibold text-slate-800">{c.sessions.total}</span></div>
                        <div>Completed: <span className="font-semibold text-emerald-700">{c.sessions.completed}</span></div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-3">
                    {c.student_code && (
                      <div className="flex items-center gap-2 text-xs">
                        <Award className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-slate-500">Student code:</span>
                        <code className="px-2 py-0.5 rounded bg-slate-100 font-mono">{c.student_code}</code>
                      </div>
                    )}
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Recent Logins</p>
                      {c.login_history?.length ? (
                        <div className="space-y-1.5">
                          {c.login_history.slice(0, 5).map((ev, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                              <LogIn className="w-3.5 h-3.5 text-slate-400" />
                              <span className="capitalize">{(ev.event_type || 'login').replace(/_/g, ' ')}</span>
                              <span className="text-slate-400">·</span>
                              <span>{(ev.timestamp || '').replace('T', ' ').split('.')[0]}</span>
                              {!ev.success && <Badge className="bg-red-100 text-red-700 h-5 text-[10px] px-1.5">Failed</Badge>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 italic">No child login events yet.</p>
                      )}
                    </div>
                    {c.last_activity && (
                      <div className="flex items-center gap-2 text-xs text-slate-500 pt-2 border-t">
                        <PlayCircle className="w-3.5 h-3.5" />
                        Last activity: {(c.last_activity.started_at || '').replace('T', ' ').split('.')[0]}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="activity">
            <Card className="mt-4">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4" /> Recent Activity Sessions</CardTitle>
                  <div className="flex gap-1">
                    {[
                      { key: '', label: 'All' },
                      { key: 'completed', label: 'Completed' },
                      { key: 'in_progress', label: 'In Progress' },
                    ].map((f) => (
                      <Button
                        key={f.key || 'all'}
                        size="sm"
                        variant={sessionFilter === f.key ? 'default' : 'outline'}
                        onClick={() => applySubFilter(f.key)}
                        data-testid={`session-filter-${f.key || 'all'}`}
                        className="h-8"
                      >
                        {f.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {sessionsLoading ? (
                  <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
                ) : sessions.length === 0 ? (
                  <p className="text-sm text-slate-400 italic text-center py-8">No sessions match this filter.</p>
                ) : (
                  <div className="divide-y">
                    {sessions.map((s, i) => (
                      <div key={i} className="py-3 flex items-center justify-between gap-3 text-sm">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-800 truncate">
                            {s.activity_title || s.activity_id || 'Untitled activity'}
                          </div>
                          <div className="text-xs text-slate-500">
                            {s.child_name} · <span className="capitalize">{s.subject || '—'}</span> · {(s.started_at || '').replace('T', ' ').split('.')[0]}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {s.score !== undefined && s.score !== null && (
                            <Badge variant="outline" className="text-xs">{s.score}%</Badge>
                          )}
                          <Badge className={
                            s.status === 'completed' ? 'bg-emerald-100 text-emerald-700'
                            : s.status === 'in_progress' ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                          }>{(s.status || 'started').replace('_', ' ')}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subscription">
            <Card className="mt-4">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><CreditCard className="w-4 h-4" /> Subscription</CardTitle>
                  <Button size="sm" onClick={() => setSubDialogOpen(true)} data-testid="manage-subscription-inline-btn">
                    <Edit3 className="w-4 h-4 mr-1" /> Manage
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row label="Plan" value={<Badge variant="outline" className="capitalize">{subscription.plan}</Badge>} />
                <Row label="Status" value={subscription.status || '—'} />
                <Row label="Stripe Customer" value={subscription.stripe_customer_id || '—'} mono />
                <Row label="Stripe Subscription" value={subscription.stripe_subscription_id || '—'} mono />
                <Row label="Period end" value={subscription.current_period_end || '—'} />
                <Row label="Trial end" value={subscription.trial_end || '—'} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="logins">
            <Card className="mt-4">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><LogIn className="w-4 h-4" /> Recent Parent Logins</CardTitle></CardHeader>
              <CardContent>
                {login_history?.length ? (
                  <div className="divide-y">
                    {login_history.map((ev, i) => (
                      <div key={i} className="flex items-center justify-between py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span className="capitalize">{(ev.event_type || 'login').replace(/_/g, ' ')}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <span>{(ev.timestamp || '').replace('T', ' ').split('.')[0]}</span>
                          {ev.success === false && <Badge className="bg-red-100 text-red-700 h-5 px-1.5 text-[10px]">Failed</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">No login events yet.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Subscription management dialog */}
      <Dialog open={subDialogOpen} onOpenChange={setSubDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="subscription-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-500" /> Manage Subscription
            </DialogTitle>
            <DialogDescription>
              Override this user&apos;s plan, status, or dates. Changes are audit-logged. Does not sync to Stripe — use this for comps, disputes, and manual grants.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="sub-plan" className="text-xs">Plan</Label>
                <Select value={subForm.plan} onValueChange={(v) => setSubForm(f => ({ ...f, plan: v }))}>
                  <SelectTrigger id="sub-plan" data-testid="sub-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="family">Family</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="sub-status" className="text-xs">Status</Label>
                <Select value={subForm.status} onValueChange={(v) => setSubForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger id="sub-status" data-testid="sub-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="trialing">Trialing</SelectItem>
                    <SelectItem value="past_due">Past Due</SelectItem>
                    <SelectItem value="canceled">Canceled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="sub-period-end" className="text-xs">Period Ends</Label>
                <Input
                  id="sub-period-end"
                  type="date"
                  value={subForm.current_period_end}
                  onChange={(e) => setSubForm(f => ({ ...f, current_period_end: e.target.value }))}
                  data-testid="sub-period-end-input"
                />
              </div>
              <div>
                <Label htmlFor="sub-trial-end" className="text-xs">Trial Ends</Label>
                <Input
                  id="sub-trial-end"
                  type="date"
                  value={subForm.trial_end}
                  onChange={(e) => setSubForm(f => ({ ...f, trial_end: e.target.value }))}
                  data-testid="sub-trial-end-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="sub-reason" className="text-xs">Reason (audit log)</Label>
              <Textarea
                id="sub-reason"
                placeholder="e.g., 3-month comp for partnership pilot"
                value={subForm.reason}
                onChange={(e) => setSubForm(f => ({ ...f, reason: e.target.value }))}
                rows={2}
                data-testid="sub-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubDialogOpen(false)} disabled={subSaving}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={saveSubscription} disabled={subSaving} className="bg-emerald-500 hover:bg-emerald-600" data-testid="save-subscription-btn">
              {subSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatTile = ({ label, value, icon: Icon, color, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="w-full text-left"
  >
    <Card className="border-0 shadow-sm hover:shadow-md hover:border-slate-300 transition-all cursor-pointer group">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
            <p className="text-2xl font-bold text-slate-800">{value}</p>
          </div>
          <Icon className={`w-8 h-8 text-${color}-500 opacity-70 group-hover:opacity-100 group-hover:scale-110 transition-all`} />
        </div>
      </CardContent>
    </Card>
  </button>
);

const Row = ({ label, value, mono }) => (
  <div className="flex items-center justify-between py-1 border-b last:border-0">
    <span className="text-slate-500">{label}</span>
    <span className={`text-slate-800 ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
  </div>
);

export default AdminUserProfile;
